import { Chat } from "@ai-sdk/react";
import { createIdGenerator } from "ai";
import type { ChatTransport } from "ai";
import {
  lumeAgentUrl,
  resolveThread,
  rotateThread,
  type AgentThread,
  type ThreadEndpoint,
  type ThreadKey,
} from "./agent-session";
import { MastraChatTransport } from "./mastra-transport";
import { openThreadHistory, ThreadHistory } from "./thread-history";
import { NotConnectedChatTransport } from "./transport";
import type { LumeUIMessage } from "./types";

/**
 * Message persistence, per the AI SDK's documented pattern — and the storage IS
 * the agent's own memory now:
 *
 *   load  -> openThreadHistory() -> safeValidateUIMessages -> Chat({ messages })
 *   save  -> nothing. The service writes each turn to the thread as it happens.
 *
 * This file used to keep a sessionStorage copy, because there was no chat backend
 * to hold one. That copy was a second version of the conversation which the agent
 * knew nothing about, and the caderno drawer could not safely restore it: the
 * transcript and the agent's memory of it were free to disagree. With observational
 * memory on the service (one thread per caderno), there is one conversation and the
 * drawer reads it.
 *
 * Chat instances are still module-scoped, so they survive route changes: clicking
 * from a tópico into its quiz unmounts the page component, not the conversation, and
 * a response still streaming keeps streaming. What changed is the reload path — it
 * now goes to the server instead of to storage.
 *
 * KEYED BY SURFACE AND BY THREAD. Lume holds more than one conversation, in two
 * senses. The onboarding thread is about building a caderno and opens with the
 * create_caderno widget; the caderno drawer is a study tutor sitting next to the
 * student's work — same assistant, different subjects, so merging them would put
 * "vamos montar o seu caderno?" at the top of a drawer opened to ask about
 * photosynthesis. And WITHIN the caderno drawer the student can now walk back
 * through their last conversations, each of which is its own thread and its own
 * Chat: switching to one and back must return the conversation as it was, not
 * re-read it.
 */

export const ONBOARDING_CHAT_ID = "lume-onboarding";

/** One conversation per caderno: a Math caderno's chat is not a History one's. */
export function cadernoChatId(cadernoId: number): string {
  return `lume-caderno-${cadernoId}`;
}

/**
 * Lume, as Mastra registers it — ONE agent, on every screen.
 *
 * Named here rather than at each call site because several of them have to agree:
 * each surface's session and the query that lists that surface's past
 * conversations. An agent id that drifted between them would list one agent's
 * threads next to another's chat, which looks like history that has gone missing.
 *
 * THE ID SAYS "caderno" AND MEANS "LUME". There used to be a second agent, `hub`,
 * for the screens outside a caderno — a greeter with three tools whose job was to
 * route the student into one. It was merged into the tutor: the Tutor is a page at
 * both levels now, so a student on /lume/chat asks what a student in the drawer
 * asks, and answering them with a thinner Lume was a seam in the product. The name
 * stayed because renaming it would strand every thread already addressed under it
 * (mastra-service/src/mastra/agents/lume-agent.ts).
 *
 * Nothing is lost by pointing the hub surface here: `mastra_threads` has no agent
 * column, so the hub's existing conversations are found under this id, with their
 * messages. The service also still answers to `hub` for one release, so the two
 * repos can deploy in either order.
 */
export const LUME_AGENT_ID = "caderno";

/**
 * ONE conversation outside every caderno, for the hub's Tutor page.
 *
 * No id to compose it from — the thread is found by its `{ hub: true }` tag and
 * the service creates it the first time. Kept apart from ONBOARDING_CHAT_ID
 * above, which talks over the same tag but seeds a greeting and a create-caderno
 * widget: same room, different opening.
 */
export const HUB_CHAT_ID = "lume-hub";

/**
 * WHICH LUME this is — the tutor of one caderno, or the one outside them all.
 *
 * Everything that differs between the two follows from it: the chat id that keys
 * the module-scoped Chat, the tag its threads carry, and the query key its
 * conversation list is cached under. They are resolved in one place
 * (chatSurfaceSpec) because they have to agree — a chat id that drifted from the
 * tag would list one surface's threads beside another's chat, which looks like
 * history that has gone missing.
 *
 * THE AGENT IS NO LONGER ONE OF THE DIFFERENCES. Both surfaces talk to the same
 * Lume; a caderno conversation and a hub conversation are two conversations with
 * one tutor, not two tutors. See LUME_AGENT_ID.
 */
export type LumeChatSurface =
  | { kind: "caderno"; cadernoId: number }
  | { kind: "hub" };

export interface ChatSurfaceSpec {
  /** The module-scope key for this surface's Chat instances */
  id: string;
  agentId: string;
  threadKey: ThreadKey;
}

export function chatSurfaceSpec(surface: LumeChatSurface): ChatSurfaceSpec {
  if (surface.kind === "caderno") {
    return {
      id: cadernoChatId(surface.cadernoId),
      agentId: LUME_AGENT_ID,
      threadKey: { cadernoId: surface.cadernoId },
    };
  }

  // Same agent as above, deliberately: what differs outside a caderno is the
  // conversation, not the tutor. The thread key is what keeps the two apart.
  return { id: HUB_CHAT_ID, agentId: LUME_AGENT_ID, threadKey: { hub: true } };
}

/** What the conversation list for a surface is cached under. */
export function chatSurfaceKey(surface: LumeChatSurface): string | number {
  return surface.kind === "caderno" ? surface.cadernoId : "hub";
}

/**
 * A live conversation: the chat itself, which thread it is written to, and how to
 * read further back into it.
 *
 * The thread id is handed out rather than kept private because the surface has
 * things to do with it that this file should not decide — naming the conversation
 * once the student has asked their first question, and marking it as the current
 * one in the picker.
 */
export interface LumeChatSession {
  chat: Chat<LumeUIMessage>;
  /** The conversation on the service, or null on a surface with no memory. */
  threadId: string | null;
  /** Everything above the newest page — see agent/thread-history.ts. */
  history: ThreadHistory;
}

interface SessionConfig {
  id: string;
  /**
   * The opening turn, for a surface that has one — the onboarding thread, whose
   * first message carries the create-caderno widget the whole flow hangs on.
   *
   * Optional because the caderno drawer has none: it opens empty and waits for
   * the student to say the first word.
   */
  seedMessages?: () => LumeUIMessage[];
  /**
   * Where replies come from, stated per surface rather than inferred.
   *
   * `agent` is the real thing and has no fallback: if the service is unreachable the
   * student sees an error, because a canned reply pretending to be a tutor is worse
   * than an honest failure — it looks like Lume answered and had nothing to say.
   *
   * `stub` is left from when the onboarding thread had no agent behind it. It has
   * one now — the same Lume as everywhere else — so nothing sets this today; kept
   * because a canned thread is still the right shape for a surface that gets built
   * before its agent does.
   */
  reply: { kind: "agent"; agentId: string } | { kind: "stub"; text: string[] };
  /**
   * What this conversation is about, so the service can find or create its thread.
   *
   * A KEY, NOT AN ID. The thread's id is generated by the service and looked up by
   * this metadata (agent/agent-session.ts) — which is what lets "nova conversa"
   * rotate to a new thread without the client remembering anything.
   *
   * THE SERVER HOLDS THE TRANSCRIPT. There is no client-side copy any more: the
   * agent writes every turn to the thread and the drawer reads it back, so what is
   * restored is by definition what the model was given. That is what made restoring
   * safe at all — the caderno drawer used to refuse it, because the only history was
   * a sessionStorage copy the agent knew nothing about, and the two could
   * contradict each other.
   *
   * Null on the stub surfaces, which have no agent and therefore no memory.
   */
  threadKey: ThreadKey | null;
  /**
   * Continue THIS conversation, rather than whichever is newest.
   *
   * Set by the picker at the top of the drawer and by nothing else. It skips the
   * lookup entirely — the thread is already known, so there is no list to read —
   * and it is the one case where the drawer opens a thread that is not the last one
   * the student was in.
   */
  threadId?: string;
  /**
   * Start a NEW thread instead of continuing the newest one.
   *
   * Set by "nova conversa" and nothing else. The old thread is superseded rather
   * than deleted: the student gets a clean panel and the agent stops carrying the
   * previous exchange into it.
   */
  rotate?: boolean;
  /**
   * How long the newest thread may have been idle and still be resumed.
   *
   * Set by the Tutor page (TUTOR_RESUME_WINDOW_MS) and by nobody else. Omitted
   * everywhere else, which takes the thread key's own default: three hours on the
   * hub, never for a caderno.
   *
   * A SESSION-CACHE BUSTER AS WELL AS A LOOKUP RULE — see cachedSession. The
   * drawer and the caderno's Tutor page share one surface id and one cached
   * session, so without that the page would be handed whatever the drawer had
   * already resolved and the window would never be consulted.
   */
  resumeWindowMs?: number;
}

/** Every conversation this tab has opened, by surface and thread. */
const sessions = new Map<string, LumeChatSession>();
const loading = new Map<string, Promise<LumeChatSession>>();

/**
 * Which conversation each surface is currently in.
 *
 * What makes the drawer come back where it was left. Walking from a tópico into its
 * quiz remounts the surface, and without this it would resolve "the newest thread"
 * again — quietly undoing a switch the student made in the picker two clicks
 * earlier, and costing a request to do it.
 */
const current = new Map<string, string>();

/**
 * A built session, and whether it is worth keeping.
 *
 * `complete` is false when the thread's history could not be read. Such a session
 * still works — the student can talk, and the agent still has the thread — but it
 * is NOT cached, so the next mount tries the history again instead of freezing a
 * half-restored conversation for as long as the tab lives.
 */
interface BuiltSession {
  session: LumeChatSession;
  complete: boolean;
}

export function loadChatSession(
  config: SessionConfig
): Promise<LumeChatSession> {
  const cached = cachedSession(config);

  if (cached !== null) {
    return Promise.resolve(cached);
  }

  // Concurrent callers (StrictMode's double effect, an impatient second click)
  // must not build two chats — or, on "nova conversa", open two threads
  const key = pendingKey(config);
  const inFlight = loading.get(key);

  if (inFlight) {
    return inFlight;
  }

  const created = createSession(config).then((built) => {
    const settled = sessionKey(config.id, built.session.threadId);

    if (built.complete) {
      sessions.set(settled, built.session);
    }

    current.set(config.id, settled);
    loading.delete(key);

    return built.session;
  });

  loading.set(key, created);

  return created;
}

/**
 * The conversation this call is asking for, if this tab already has it.
 *
 * Never on "nova conversa": the whole point there is a thread that does not exist
 * yet, so a cache hit would hand back the conversation being left.
 */
function cachedSession(config: SessionConfig): LumeChatSession | null {
  if (config.rotate === true) {
    return null;
  }

  // A WINDOW MEANS "CHECK, DO NOT ASSUME". The caderno drawer and the caderno's
  // Tutor page are one surface id (chatSurfaceSpec) and therefore one cached
  // session — which is what makes them the same conversation, and is right. But it
  // means the page arriving would be handed the drawer's already-resolved thread
  // without the age of it ever being looked at, and a week-old exchange would be on
  // screen exactly where the page's whole rule says it should not be.
  //
  // So a caller that states a window always re-resolves. It costs one thread list
  // read per arrival on the Tutor page and nothing anywhere else, and the resolve
  // usually lands on the same thread anyway — in which case the sessions map still
  // has it, keyed by thread, and no chat is rebuilt.
  if (config.resumeWindowMs !== undefined && config.threadId === undefined) {
    return null;
  }

  const key =
    config.threadId === undefined
      ? current.get(config.id)
      : sessionKey(config.id, config.threadId);

  if (key === undefined) {
    return null;
  }

  const session = sessions.get(key);

  if (session === undefined) {
    return null;
  }

  // Re-marked as current, because asking for a thread by id IS switching to it
  current.set(config.id, key);

  return session;
}

/** One surface's one conversation. `none` covers the surfaces with no memory. */
function sessionKey(surfaceId: string, threadId: string | null): string {
  return `${surfaceId}#${threadId ?? "none"}`;
}

/**
 * What two concurrent calls have to agree on to be recognised as the same load.
 *
 * Not the session key, because the thread is usually still unknown at this point.
 * Rotation gets its own so that a "nova conversa" cannot be answered by the load
 * that was already resolving the OLD thread.
 */
function pendingKey(config: SessionConfig): string {
  if (config.threadId !== undefined) {
    return sessionKey(config.id, config.threadId);
  }

  return config.rotate === true
    ? `${config.id}#rotating`
    : `${config.id}#resolving`;
}

async function createSession(config: SessionConfig): Promise<BuiltSession> {
  // The thread is resolved ONCE, here, and then used for both halves: the history
  // this session opens with, and the thread every turn is written to. Resolving it
  // twice is how the drawer would end up displaying one conversation while sending
  // to another.
  const thread = await resolveThreadFor(config);
  const endpoint = agentEndpoint(config);

  const history =
    thread === null || endpoint === null
      ? null
      : await openThreadHistory(endpoint, thread.id);

  // Three outcomes, and the middle one is the one that used to be silent: a
  // conversation exists but we could not read it. Saying nothing there meant the
  // panel looked like a fresh start while every turn continued the old thread.
  const seed = config.seedMessages?.() ?? [];
  const messages =
    history === null || history.status === "unreadable"
      ? [...seed, historyUnavailableNotice()]
      : history.messages.length > 0
        ? history.messages
        : seed;

  return {
    complete: history === null || history.status === "read",
    session: {
      threadId: thread?.id ?? null,
      history:
        history !== null && history.status === "read"
          ? history.history
          : ThreadHistory.none(),
      chat: new Chat<LumeUIMessage>({
        id: config.id,
        // Readable, collision-free ids for messages created on this side
        generateId: createIdGenerator({ prefix: "msg", size: 16 }),
        messages,
        transport: transportFor(config, thread?.id ?? null),
        // No onFinish: the agent service persists each turn to the thread as it
        // happens, so there is nothing for this side to write. A local copy would
        // only be a second version of the same conversation.
      }),
    },
  };
}

/**
 * Shown when the thread exists but its messages could not be read.
 *
 * It says the true thing, which is not "you are starting over": the conversation is
 * still on the server and the agent still has it, so the next reply will refer to
 * things that are not on screen. Without this the student is misled twice over —
 * into thinking the past exchange is gone, and then by an answer that assumes it.
 *
 * Local only. The transport sends the newest message and nothing else, so this never
 * reaches the model.
 */
function historyUnavailableNotice(): LumeUIMessage {
  return {
    id: "lume-history-unavailable",
    role: "assistant",
    parts: [
      {
        type: "text",
        text:
          "Não consegui carregar aqui as mensagens da nossa conversa anterior — " +
          "mas ela continua guardada e eu ainda me lembro dela, então pode " +
          'seguir de onde parou. Se preferir começar do zero, use "Nova ' +
          'conversa".',
      },
    ],
  };
}

/** The conversation this session continues, or starts. Null = no memory available. */
async function resolveThreadFor(
  config: SessionConfig
): Promise<AgentThread | null> {
  const endpoint = agentEndpoint(config);

  if (endpoint === null || config.threadKey === null) {
    return null;
  }

  // Named by the picker: no lookup at all, which is what makes switching back to a
  // conversation cost one read of its messages and nothing else
  if (config.threadId !== undefined) {
    return { id: config.threadId };
  }

  return config.rotate === true
    ? rotateThread(endpoint, config.threadKey)
    : resolveThread(endpoint, config.threadKey, config.resumeWindowMs);
}

/** The service and agent this surface talks to, or null when it talks to neither. */
function agentEndpoint(config: SessionConfig): ThreadEndpoint | null {
  return config.reply.kind === "agent"
    ? { baseUrl: lumeAgentUrl(), agentId: config.reply.agentId }
    : null;
}

function transportFor(
  config: SessionConfig,
  threadId: string | null
): ChatTransport<LumeUIMessage> {
  if (config.reply.kind === "stub") {
    return new NotConnectedChatTransport(config.reply.text);
  }

  // Note what is NOT here: a fallback when the URL is missing. An unconfigured or
  // unreachable agent surfaces as an error in the drawer, which is the truth. The
  // transport itself carries that decision, so a misconfigured environment fails
  // the same way a dead service does.
  return new MastraChatTransport(lumeAgentUrl(), config.reply.agentId, threadId);
}


/**
 * Drops every in-memory conversation.
 *
 * Called on logout, so it clears ALL of them: the next student on this tab must not
 * find the previous one's chat, in any surface — and must not find the picker still
 * pointing at a thread that was theirs.
 *
 * Nothing to sweep from storage any more — there is no local copy. The threads
 * themselves stay on the server, which is correct: they belong to the student who
 * logged out, are scoped to their session, and are theirs again next time they sign
 * in. What matters here is that this tab forgets them.
 */
export function resetChatSession(): void {
  sessions.clear();
  loading.clear();
  current.clear();
}
